<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal
      @action="buildFilters()"
      @clear="clearSearchFilters()"
    >
      <template v-slot:main-filters>
        <filters
          :tenant-filter.sync="selectedTenants"
          :tenant-options="tenantOptions"
          :start-datetime.sync="startDatetimeFilter"
          :end-datetime.sync="endDatetimeFilter"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card-group deck class="mx-1">
      <b-card class="mt-1" no-body>
        <!-- Table Top -->
        <b-row class="m-2">
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }} </label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              data-cy="per-page-selector"
            />
            <label class="text-capitalize">{{ $t("table.entries") }} </label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
                data-cy="search"
              />
            </div>
          </b-col>
        </b-row>
        <!-- Table Container Card -->
        <b-table
          ref="refListTable"
          class="position-relative"
          :items="fetchList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          :empty-text="$t('no_record_found')"
          :sort-desc.sync="isSortDirDesc"
          @row-selected="onRowSelected"
          select-mode="single"
          selectable
        >
          <template #cell(validated)="data">
            <feather-icon
              v-if="data.item.validated_at"
              icon="CheckCircleIcon"
              class="ml-1"
              size="17"
              style="color: green"
            />
            <feather-icon
              v-else-if="data.item.answered_at"
              icon="MinusCircleIcon"
              class="ml-1"
              size="17"
              style="color: orange"
            />
            <feather-icon
              v-else
              :id="`validated-icon-${data.item.id || data.index}`"
              icon="MinusCircleIcon"
              class="ml-1"
              size="17"
              style="color: red"
              v-b-tooltip.hover
              :title="data.item.deadline ? getDeadlineTooltip(data.item.deadline) : ''"
            />

          </template>
          <template #cell(deadline)="data">
            <span
              v-if="data.item.deadline"
              :id="`deadline-${data.item.id || data.index}`"
              v-b-tooltip.hover
              :title="getDeadlineTooltip(data.item.deadline)"
              style="cursor: pointer;"
            >
              {{ formatDeadlineDate(data.item.deadline) }}
            </span>
            <span v-else>-</span>
          </template>
          <template #cell(data)="data">
          <pre v-if="data.item.data">{{
              JSON.stringify(data.item.data, null, "\t")
            }}</pre>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
            <span class="text-muted text-capitalize">{{
                $t("table.showing", {
                  from: dataMeta.from,
                  to: dataMeta.to,
                  of: dataMeta.of,
                })
              }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <Pagination
                :totalList="totalList"
                :currentPage="currentPage"
                @updatePagination="update"
                :perPage="perPage"
              >
              </Pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
      <b-card class="mt-1" v-if="selectedObject" no-body>
        <div class="d-flex justify-content-between">
          <div class="p-2">
            <p
              v-if="selectedObject.data.validated_at"
              class="mb-1"
            >
              {{ $t("action_plan.validatedBy") }}:
              <b-badge variant="primary">{{
                  selectedObject.data.user_validation.name
                }}
              </b-badge>
            </p>
            <p v-if="selectedObject.data.validated_at">
              <span class="small">{{ selectedObject.data.validated_at }}</span>
            </p>
          </div>
          <feather-icon
            icon="XIcon"
            class=" mt-1 mr-2"
            style="cursor: pointer"
            size="30"
            @click="selectedObject = null"
          />
        </div>
        <div class="mx-2">
          <action-plan-slot
            :data="selectedObject"
            :preview="true"
            :validating="true"
            current-location="action_plan_logs"
            @validate-success="refetchData"/>
        </div>
      </b-card>
    </b-card-group>
  </div>
</template>

<script>
import {BCard, BRow, BCol, BTable, BPagination, VBTooltip} from "bootstrap-vue";
import vSelect from "vue-select";
import {ref, nextTick} from "@vue/composition-api";
import useList from "./useList";
import Pagination from "@/components/Pagination.vue";
import InfoSlot from "@/views/components/GridView/layouts/slots/InfoSlot";
import Filters from "./Filters.vue";
import {useUtils as useI18nUtils} from "@core/libs/i18n";
import {SLOT_TYPES} from "@/config/layoutConfig";
import ActionPlanSlot from "@/views/components/GridView/layouts/slots/ActionPlanSlot.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import moment from "moment-timezone";

export default {
  components: {
    Filters,
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BTable,
    BPagination,
    Pagination,
    vSelect,
    InfoSlot,
    ActionPlanSlot,
    FilterModal,
    ButtonFilters,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },

  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const {t} = useI18nUtils();

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      selectedTenants,
      startDatetimeFilter,
      endDatetimeFilter,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      lastLogSelected,
      selectedObject,
      getDeadlineTooltip
    } = useList();

    const formatDeadlineDate = (date) => {
      if (!date) return '-';
      return moment(date).format('YYYY-MM-DD');
    };

    const onRowSelected = (item) => {
      const [log] = item;

      if (log) {
        lastLogSelected.value = log;
      }

      nextTick(() => {
        selectedObject.value = {
          data: {
            id_action_plan: lastLogSelected.value.id_action_plan,
            uuid_action_plan_log: lastLogSelected.value.uuid,
            type: SLOT_TYPES.ACTION_PLAN,
            validated_at: lastLogSelected.value.validated_at,
            user_validation: lastLogSelected.value.user_validation
          },
        };
      });
    };

    return {
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      selectedTenants,
      startDatetimeFilter,
      endDatetimeFilter,
      onRowSelected,
      lastLogSelected,
      selectedObject,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      getDeadlineTooltip,
      formatDeadlineDate,
    };
  },
};
</script>
